import axios from 'axios';
import { setUser, clearUser, captureError } from './sentry';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth interceptor
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Capture API errors in Sentry
    if (error.response?.status >= 500) {
      captureError(error, {
        endpoint: error.config?.url,
        method: error.config?.method,
        status: error.response?.status,
      });
    }
    
    // Only redirect on explicit auth failure, not network errors
    if (error.response?.status === 401) {
      // Don't redirect if we're already on login/signup pages
      const currentPath = window.location.pathname;
      if (!currentPath.includes('/login') && !currentPath.includes('/signup')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        clearUser(); // Clear Sentry user context
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: async (data) => {
    const response = await api.post('/auth/login', data);
    // Set Sentry user context on successful login
    if (response.data?.user) {
      setUser(response.data.user);
    }
    return response;
  },
  getMe: async () => {
    const response = await api.get('/auth/me');
    // Set Sentry user context
    if (response.data) {
      setUser(response.data);
    }
    return response;
  },
  updateMe: (data) => api.put('/auth/me', data),
  upgradeTier: (tier, billing_cycle = 'monthly') => 
    api.post(`/auth/upgrade-tier?tier=${tier}&billing_cycle=${billing_cycle}`),
  cancelSubscription: () => api.post('/auth/cancel-subscription'),
  requestPasswordReset: (email) => api.post('/auth/request-password-reset', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    clearUser(); // Clear Sentry user context
  },
};

// Payments API
export const paymentsAPI = {
  getPackages: () => api.get('/payments/packages'),
  createCheckout: (packageId) => api.post('/payments/checkout', {
    package_id: packageId,
    origin_url: window.location.origin
  }),
  getPaymentStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  createPortalSession: () => api.post('/payments/portal'),
};

// Projects API
export const projectsAPI = {
  create: (data) => api.post('/projects', data),
  import: (formData) => api.post('/projects/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getAll: () => api.get('/projects'),
  getOne: (id) => api.get(`/projects/${id}`),
  update: (id, data) => api.put(`/projects/${id}`, data),
  close: (id) => api.post(`/projects/${id}/close`),
  delete: (id) => api.delete(`/projects/${id}`),
  getReportData: (id) => api.get(`/projects/${id}/report-data`),
};

// Milestones API
export const milestonesAPI = {
  add: (projectId, data) => api.post(`/projects/${projectId}/milestones`, data),
  update: (projectId, milestoneId, data) => 
    api.put(`/projects/${projectId}/milestones/${milestoneId}`, data),
  delete: (projectId, milestoneId) => 
    api.delete(`/projects/${projectId}/milestones/${milestoneId}`),
};

// Risks API
export const risksAPI = {
  add: (projectId, data) => api.post(`/projects/${projectId}/risks`, data),
  update: (projectId, riskId, data) => 
    api.put(`/projects/${projectId}/risks/${riskId}`, data),
  delete: (projectId, riskId) => 
    api.delete(`/projects/${projectId}/risks/${riskId}`),
  resolve: (projectId, riskId, data) => 
    api.post(`/projects/${projectId}/risks/${riskId}/resolve`, data),
};

// Deliverables API
export const deliverablesAPI = {
  add: (projectId, data) => api.post(`/projects/${projectId}/deliverables`, data),
  delete: (projectId, deliverableId) => 
    api.delete(`/projects/${projectId}/deliverables/${deliverableId}`),
};

// Reports API
export const reportsAPI = {
  create: (data) => api.post('/reports', data),
  getByProject: (projectId) => api.get(`/projects/${projectId}/reports`),
  getOne: (id) => api.get(`/reports/${id}`),
  update: (id, data) => api.put(`/reports/${id}`, data),
  delete: (id) => api.delete(`/reports/${id}`),
  sendEmail: (id, data) => api.post(`/reports/${id}/send-email`, data),
  updateCustomSections: (id, customSections) => api.patch(`/reports/${id}/custom-sections`, { custom_sections: customSections }),  // BUG 4 fix
};

// Distribution Lists API
export const distributionListsAPI = {
  list: (projectId) => api.get(`/projects/${projectId}/distribution-lists`),
  create: (projectId, data) => api.post(`/projects/${projectId}/distribution-lists`, data),
  getOne: (projectId, listId) => api.get(`/projects/${projectId}/distribution-lists/${listId}`),
  update: (projectId, listId, data) => api.put(`/projects/${projectId}/distribution-lists/${listId}`, data),
  delete: (projectId, listId) => api.delete(`/projects/${projectId}/distribution-lists/${listId}`),
  getEmailHistory: (projectId, listId) => api.get(`/projects/${projectId}/distribution-lists/${listId}/email-history`),
  getProjectEmailHistory: (projectId) => api.get(`/projects/${projectId}/email-history`),
};

// Ideas/Feature Requests API
export const ideasAPI = {
  list: (params = {}) => api.get('/ideas', { params }),
  create: (data) => api.post('/ideas', data),
  getOne: (id) => api.get(`/ideas/${id}`),
  vote: (id) => api.post(`/ideas/${id}/vote`),
};

// Admin API
export const adminAPI = {
  // Users
  getUsers: (params = {}) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  createUser: (data) => api.post('/admin/users', data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  extendTrial: (id, days) => api.post(`/admin/users/${id}/extend-trial?days=${days}`),
  impersonateUser: (id) => api.post(`/admin/users/${id}/impersonate`),
  
  // Stats
  getStats: () => api.get('/admin/stats'),
  
  // Ideas
  getIdeas: (params = {}) => api.get('/admin/ideas', { params }),
  getIdeasStats: () => api.get('/admin/ideas/stats'),
  updateIdea: (id, data) => api.put(`/admin/ideas/${id}`, data),
  deleteIdea: (id) => api.delete(`/admin/ideas/${id}`),
};

export default api;
